import type uPlot from "uplot";
import { computeZoomSelection } from "./chartZoomSelection";

// Minimum drag distance (CSS px) before an axis is zoomed. Below this a
// press-release is treated as a click, not a zoom.
const MIN_DRAG_PX = 5;

/**
 * Drag-to-zoom that clamps the selection to the plot area.
 *
 * uPlot's built-in cursor drag binds mousemove to the `.u-over` element only, so
 * the moment the pointer crosses a plot border during a drag it stops tracking
 * and the selection freezes short of the edge (uPlot's mouseLeave only snaps to
 * the border within ~10px). That makes it hard to zoom right up to the edge.
 *
 * This plugin disables the built-in drag (the builders set
 * `cursor.drag = { x:false, y:false }`) and reimplements it with window-level
 * mousemove/mouseup tracking, so a pointer dragged past a border clamps to that
 * edge (the maximum) via {@link computeZoomSelection}. On release it applies the
 * zoom with `setScale`, which drives the same setScale hooks (zoom history,
 * refresh gate) the graph windows already rely on.
 */
export function edgeClampZoomPlugin(): uPlot.Plugin {
  let teardown: (() => void) | null = null;

  return {
    hooks: {
      init: (u: uPlot) => {
        const over = u.over;

        // Own selection box — uPlot's built-in one is disabled. Carries uPlot's
        // `u-select` class so global.css's theme-accent `.uplot .u-select` rule
        // skins it; an inline background would beat that rule and bring back
        // uPlot's 7% black wash, invisible on the dark themes.
        const box = document.createElement("div");
        box.className = "u-select";
        box.style.position = "absolute";
        box.style.pointerEvents = "none";
        box.style.display = "none";
        over.appendChild(box);

        let dragging = false;
        let startX = 0;
        let startY = 0;

        const plotRect = () => over.getBoundingClientRect();

        const drawBox = (sel: { left: number; top: number; width: number; height: number }) => {
          box.style.left = `${sel.left}px`;
          box.style.top = `${sel.top}px`;
          box.style.width = `${sel.width}px`;
          box.style.height = `${sel.height}px`;
          box.style.display = "block";
        };

        const onMove = (e: MouseEvent) => {
          if (!dragging) return;
          const r = plotRect();
          drawBox(
            computeZoomSelection(startX, startY, e.clientX - r.left, e.clientY - r.top, r.width, r.height, MIN_DRAG_PX),
          );
        };

        const onUp = (e: MouseEvent) => {
          if (!dragging) return;
          dragging = false;
          window.removeEventListener("mousemove", onMove, true);
          window.removeEventListener("mouseup", onUp, true);
          box.style.display = "none";

          const r = plotRect();
          const sel = computeZoomSelection(
            startX, startY, e.clientX - r.left, e.clientY - r.top, r.width, r.height, MIN_DRAG_PX,
          );
          if (!sel.zoomX && !sel.zoomY) return; // click / tiny drag → no zoom

          if (sel.zoomX) {
            u.setScale("x", {
              min: u.posToVal(sel.left, "x"),
              max: u.posToVal(sel.left + sel.width, "x"),
            });
          }
          if (sel.zoomY) {
            // Zoom every non-x, non-derived scale, matching uPlot's default
            // box-zoom (normalize mode has one y scale per series).
            for (const key in u.scales) {
              const sc = u.scales[key];
              if (key !== "x" && sc.from == null) {
                u.setScale(key, {
                  min: u.posToVal(sel.top + sel.height, key),
                  max: u.posToVal(sel.top, key),
                });
              }
            }
          }
        };

        const onDown = (e: MouseEvent) => {
          if (e.button !== 0) return; // left button only
          const r = plotRect();
          startX = e.clientX - r.left;
          startY = e.clientY - r.top;
          dragging = true;
          box.style.display = "none";
          window.addEventListener("mousemove", onMove, true);
          window.addEventListener("mouseup", onUp, true);
        };

        over.addEventListener("mousedown", onDown);

        teardown = () => {
          over.removeEventListener("mousedown", onDown);
          window.removeEventListener("mousemove", onMove, true);
          window.removeEventListener("mouseup", onUp, true);
          box.remove();
        };
      },
      destroy: () => {
        teardown?.();
        teardown = null;
      },
    },
  };
}
